/* eslint-disable func-names */
<template>
  <div class="detail-spreadsheet-wrapper" v-if="spreadsheet !== null">
    <h1 class="heading-page">
        {{spreadsheet.name}}
    </h1>
    <ModalConfirm
      title="Are your sure want to remove this spreadsheet from project?"
      content="This action is permanent! It cannot be undone."
      :ok="removeSpreadsheet"
      okText="Delete"
      cancelText="Cancel"
    >
      <Button class="white btn-delete" :dynamicAttrs="dynamicAttrsButtonDelete"><DeleteOutlined /> Delete</Button>
    </ModalConfirm>

    <form class="custom-form w-600" v-if="spreadsheet">
      <div class="mb-3 row">
        <label class="col-md-2 col-form-label">Name:</label>
        <div class="col-md-10 txt-right">
          <input type="text" name="project-id" v-model="spreadsheet.name" readonly class="form-control">
          <a :href="'https://docs.google.com/spreadsheets/d/' + spreadsheet.googleSpreadsheetId" class="link spreadsheet-link" target="_blank">Open Spreadsheet <LinkOutlined /></a>
        </div>
      </div>
      <div class="mb-3 row">
        <label class="col-md-2 col-form-label">Slug:</label>
        <div class="col-md-10">
          <input type="text" name="project-name" v-model="spreadsheet.slug" class="form-control col-md-3" readonly
        >
        </div>
      </div>
    </form>
    <p class="mb-5">Refresh Spreadsheet will update all current configuration (including sheets, table structure) to match with the new data from your spreadsheet</p>
    <ErrorMessage v-if="error" :messsage="error" />
    <h6 class="heading-sheet">
      Sheets
      <ModalConfirm
        title="Do you want to override with the new spreadsheet's structure?"
        content="Your Google Spreadsheet structure was changed. Overriding may causes your SQL queries that use this spreadsheet may not works"
        :ok="refreshSpreadsheet"
        okText="Refresh"
        cancelText="Cancel"
      >
        <Button :dynamicAttrs="dynamicAttrsButtonRefesh">
          <SyncOutlined /> Refresh
        </Button>
      </ModalConfirm>
    </h6>
    <a-table :columns="columns" :data-source="newListSheets" :pagination="false" size="middle" class="tbl-preview-coloumn" rowKey="slug">
      <template #slug="{ record }">
        <a :href="renderSlugSheetApi(record)" target="_blank">{{ renderSlugSheetApi(record) }}</a>
        <Button
            :dynamicAttrs="{class:'blank-button ml-5'}"
            @click="copySlugToClipBoard(renderSlugSheetApi(record))">
            <CopyOutlined />
          </Button>
      </template>
      <template #get="{ record }">
        <Checkbox v-model="record.methods.get" />
      </template>
      <template #put="{ record }">
        <Checkbox v-model="record.methods.put" />
      </template>
      <template #post="{ record }">
        <Checkbox v-model="record.methods.post" />
      </template>
      <template #delete="{ record }">
        <Checkbox v-model="record.methods.delete" />
      </template>
      <template #detail="{ record }">
        <a :onClick="goToSheetDetail(record.slug)">Detail</a>
      </template>
    </a-table>
  </div>
</template>

<script type="ts">
import { defineComponent } from 'vue';
import get from 'lodash/fp/get';
import isNil from 'lodash/fp/isNil';
import { copyToClipboard } from '@/utils/string';
import { DOMAIN_API } from '@/utils/constants';
import Button from '@/components/Button/template.vue';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import Table from '@/components/Table/template.vue';
import Checkbox from '@/components/Checkbox/template.vue';
import ErrorMessage from '@/components/ErrorMessage/template.vue';
import ModalConfirm from '@/components/ModalConfirm/template.vue';
import DeleteOutlined from '@ant-design/icons-vue/DeleteOutlined';
import SyncOutlined from '@ant-design/icons-vue/SyncOutlined';
import LinkOutlined from '@ant-design/icons-vue/LinkOutlined';
import CopyOutlined from '@ant-design/icons-vue/CopyOutlined';
import { message } from 'ant-design-vue';
import Project from '../../../models/Project';
import { spreadsheetsMapper } from '../../../store/Spreadsheet';

import './styles.scss';

export default defineComponent({
  name: 'SpreadsheetNew',
  components: {
    Button,
    ErrorMessage,
    DeleteOutlined,
    LinkOutlined,
    Checkbox,
    SyncOutlined,
    CopyOutlined,
    ModalConfirm,
  },
  props: {
    project: {
      type: Project,
    },
  },
  data() {
    return {
      DOMAIN_API,
      dynamicAttrsButtonRefesh: {
        loading: false,
        class: 'medium',
      },
      dynamicAttrsButtonDelete: {
        loading: false,
        class: 'medium',
      },
      spreadsheet: null,
      loading: false,
      error: null,
      columns: [
        {
          title: 'Name',
          dataIndex: 'title',
          key: 'title',
          width: '20%',
        },
        {
          title: 'Slug',
          dataIndex: 'slug',
          key: 'slug',
          slots: { customRender: 'slug' },
        },
        {
          title: 'GET',
          key: 'get',
          slots: { customRender: 'get' },
          width: 70,
        },
        {
          title: 'PUT',
          key: 'put',
          slots: { customRender: 'put' },
          width: 70,
        },
        {
          title: 'POST',
          key: 'post',
          slots: { customRender: 'post' },
          width: 70,
        },
        {
          title: 'DELETE',
          key: 'delete',
          slots: { customRender: 'delete' },
          width: 70,
        },
        {
          key: 'detail',
          slots: { customRender: 'detail' },
          width: 70,
        },
      ],
    };
  },
  watch: {
    '$route.params.spreadsheetSlug': function (newSpreadsheetSlug) {
      if (!isNil(newSpreadsheetSlug)) this.fetchSpreadsheet();
    },
    spreadsheet: {
      async handler(newValue, oldValue) {
        if (oldValue === null || get('slug', newValue) !== get('slug', oldValue)) return; // Not call when first fetch
        const { data, error } = await this.updateSpreadsheet({
          projectSlug: this.$route.params.slug,
          spreadsheetSlug: this.$route.params.spreadsheetSlug,
          spreadsheet: newValue,
        });
        if (data) {
          message.success('Update spreadsheet successfully');
        } else {
          this.error = error;
          message.error('Update spreadsheet failed');
        }
      },
      deep: true,
    },
  },
  computed: {
    ...spreadsheetsMapper.mapState(['getCurrentSpreadsheet']),
    newListSheets() {
      return this.spreadsheet.sheets;
    },
  },
  methods: {
    ...spreadsheetsMapper.mapActions(['fetchSpreadsheetDetail', 'updateSpreadsheet', 'deleteSpreadsheet']),
    async fetchSpreadsheet() {
      this.loading = true;
      const { data, error } = await this.fetchSpreadsheetDetail({
        projectSlug: Array.isArray(this.$route.params.slug) ? this.$route.params.slug[0] : this.$route.params.slug,
        spreadsheetSlug: Array.isArray(this.$route.params.spreadsheetSlug) ? this.$route.params.spreadsheetSlug[0] : this.$route.params.spreadsheetSlug,
      });

      if (data) {
        this.spreadsheet = data;
        this.error = null;
      } else {
        this.spreadsheet = null;
        this.error = error;
      }
      this.loading = false;
    },
    async refreshSpreadsheet() {
      this.dynamicAttrsButtonRefesh.loading = true;
      const { data, error } = await this.fetchSpreadsheetDetail({
        projectSlug: Array.isArray(this.$route.params.slug) ? this.$route.params.slug[0] : this.$route.params.slug,
        spreadsheetSlug: Array.isArray(this.$route.params.spreadsheetSlug) ? this.$route.params.spreadsheetSlug[0] : this.$route.params.spreadsheetSlug,
        isRefresh: true,
      });

      if (data) {
        this.spreadsheet = data;
        this.error = null;
      } else {
        this.spreadsheet = null;
        this.error = error;
      }
      this.dynamicAttrsButtonRefesh.loading = false;
    },
    renderSlugSheetApi(sheet) {
      return `${DOMAIN_API}/${this.project.slug}/sp/${this.spreadsheet.slug}/${sheet.slug}`;
    },
    async removeSpreadsheet() {
      this.dynamicAttrsButtonDelete.loading = true;
      const { data, error } = await this.deleteSpreadsheet({
        projectSlug: Array.isArray(this.$route.params.slug) ? this.$route.params.slug[0] : this.$route.params.slug,
        spreadsheetSlug: Array.isArray(this.$route.params.spreadsheetSlug) ? this.$route.params.spreadsheetSlug[0] : this.$route.params.spreadsheetSlug,
      });
      if (data) {
        message.success('Delete spreadsheet successfully');
        this.$router.push(`/projects/${this.$route.params.slug}`);
      } else {
        this.error = error;
        message.error('Delete spreadsheet failed');
      }
      this.dynamicAttrsButtonDelete.loading = false;
    },
    goToSheetDetail(sheetSlug) {
      return () => this.$router.push(`/projects/${this.$route.params.slug}/spreadsheets/${this.$route.params.spreadsheetSlug}/${sheetSlug}`);
    },
    copySlugToClipBoard(slug) {
      copyToClipboard(slug);
      message.success('Copied');
    },
  },
  mounted() {
    this.fetchSpreadsheet();
  },
});
</script>
